import MUISwitch from '@mui/material/Switch';
import { useController, useFormContext } from 'react-hook-form';
import StyledSwitch from './StyledSwitch';

interface SwitchProps {
  name: string;
  label?: React.ReactNode;
  required?: boolean;
  bold?: boolean;
  onChange?: () => void;
  onClick?: () => void;
}

// Same compact switch used on the agents list (Call/Chat tables, Live Sync).
// Track blue uses --primary-color so form and list UIs stay on one brand blue.
const platformSwitchSx = {
  width: 42,
  height: 22,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: '2px',
    transitionDuration: '180ms',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#FFFFFF',
      '& + .MuiSwitch-track': {
        backgroundColor: 'var(--primary-color)',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 18,
    height: 18,
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.25)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 11,
    backgroundColor: '#D1D5DB',
    opacity: 1,
  },
};

const Switch = ({
  name,
  label = '',
  required = false,
  bold,
  onChange,
  onClick,
}: SwitchProps): JSX.Element => {
  const { control } = useFormContext();
  // MUI's Switch forwards a spread `ref` to its root <span>, not to the
  // underlying <input>, so react-hook-form's register() can never apply the
  // field's default value as the initial `checked` state on mount (it always
  // renders unchecked regardless of stored data). useController sidesteps
  // that by driving `checked` explicitly off field.value instead of relying
  // on ref-based defaultChecked.
  const { field } = useController({ name, control, rules: { required } });

  return (
    <StyledSwitch
      required={required}
      bold={bold}
      control={
        <MUISwitch
          name={field.name}
          checked={!!field.value}
          onBlur={field.onBlur}
          ref={field.ref}
          onChange={event => {
            field.onChange(event);
            onChange?.();
          }}
          onClick={onClick}
          sx={platformSwitchSx}
        />
      }
      label={label}
    />
  );
};

export default Switch;
